import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { format } from 'date-fns';
import {
    list_all_requests,
    update_template_request_status
} from '@/api/template-requests';
import { TemplateRequest, TemplateRequestStatus } from '@/types/template-request';
import { Loader2, FileText, ExternalLink, Check, X, Building2, User, FilePlus } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import { timestamp_to_date } from '@/utils/time';
import { DataTable } from '@/components/table';
import { ColumnDef } from '@tanstack/react-table';
import DataTableSkeleton from '@/components/ui/data-table-skeleton';

export function SuperAdminTemplateRequests() {
    const { t } = useTranslation();
    const [requests, setRequests] = useState<TemplateRequest[]>([]);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    // Params state combining pagination and filters
    const [params, setParams] = useState({
        page: 0,
        page_size: 10,
        search: '',
        status: 'all',
    });

    // Reject Dialog State
    const [rejectDialog, setRejectDialog] = useState<{ open: boolean, id: string | null }>({
        open: false,
        id: null
    });
    const [rejectReason, setRejectReason] = useState('');
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    const fetchRequests = async () => {
        setLoading(true);
        setError(false);
        try {
            const apiParams: any = {
                limit: params.page_size,
                offset: params.page * params.page_size,
            };
            if (params.search) apiParams.search = params.search;
            if (params.status !== 'all') apiParams.status = params.status;

            const data = await list_all_requests(apiParams);
            setRequests(data.results);
            setCount(data.count);
        } catch (error) {
            console.error('Failed to fetch requests', error);
            setError(true);
            toast({
                title: t('error'),
                description: t('error_fetching_requests', 'Failed to fetch requests'),
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchRequests();
        }, 500);
        return () => clearTimeout(timer);
    }, [params]);

    const handleApprove = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent row click if applicable
        setActionLoading(id);
        try {
            await update_template_request_status(id, { status: 'done' });
            toast({
                title: t('success'),
                description: t('request_approved'),
            });
            fetchRequests();
        } catch (error) {
            toast({
                title: t('error'),
                description: t('error_updating_request'),
                variant: 'destructive',
            });
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async () => {
        if (!rejectDialog.id) return;

        setActionLoading(rejectDialog.id);
        try {
            await update_template_request_status(rejectDialog.id, {
                status: 'refused',
                admin_notes: rejectReason
            });
            toast({
                title: t('success'),
                description: t('request_rejected'),
            });
            setRejectDialog({ open: false, id: null });
            setRejectReason('');
            fetchRequests();
        } catch (error) {
            toast({
                title: t('error'),
                description: t('error_updating_request'),
                variant: 'destructive',
            });
        } finally {
            setActionLoading(null);
        }
    };

    const getStatusBadge = (status: TemplateRequestStatus) => {
        switch (status) {
            case 'done':
                return <Badge className="bg-green-500">{t('status_done')}</Badge>;
            case 'refused':
                return <Badge variant="destructive">{t('status_refused')}</Badge>;
            default:
                return <Badge variant="secondary">{t('status_pending')}</Badge>;
        }
    };

    const columns: ColumnDef<TemplateRequest>[] = [
        {
            accessorKey: 'created_at',
            header: t('date'),
            cell: ({ row }) => format(timestamp_to_date(row.original.created_at), 'MMM dd, yyyy'),
        },
        {
            accessorKey: 'cabinet_name',
            header: t('cabinet'),
            cell: ({ row }) => (
                <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span>{row.original.cabinet_name || '-'}</span>
                </div>
            ),
        },
        {
            accessorKey: 'user_name',
            header: t('user'),
            cell: ({ row }) => (
                <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>{row.original.user_name || '-'}</span>
                </div>
            ),
        },
        {
            accessorKey: 'filename',
            header: t('file'),
            cell: ({ row }) => (
                <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="truncate max-w-[200px]" title={row.original.filename}>{row.original.filename}</span>
                    </div>
                    {row.original.description && (
                        <span className="text-xs text-muted-foreground truncate max-w-[200px]" title={row.original.description}>
                            {row.original.description}
                        </span>
                    )}
                </div>
            ),
        },
        {
            accessorKey: 'status',
            header: t('status'),
            cell: ({ row }) => getStatusBadge(row.original.status),
        },
        {
            id: 'actions',
            header: t('actions'),
            cell: ({ row }) => (
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" asChild title={t('download')} onClick={(e) => e.stopPropagation()}>
                        <a href={row.original.file_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-4 w-4" />
                        </a>
                    </Button>

                    {row.original.status === 'pending' && (
                        <>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={(e) => handleApprove(row.original.id, e)}
                                disabled={actionLoading === row.original.id}
                                title={t('approve')}
                            >
                                {actionLoading === row.original.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Check className="h-4 w-4" />
                                )}
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setRejectDialog({ open: true, id: row.original.id });
                                }}
                                disabled={actionLoading === row.original.id}
                                title={t('reject')}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </>
                    )}
                </div>
            ),
        }
    ];

    return (
        <div className="space-y-4">
            <PageHeader
                leading={
                    <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <FilePlus className="h-5 w-5" />
                    </span>
                }
                title={t('template_requests')}
            />

            <div className="flex gap-4 items-center">
                <div className="flex-1">
                    <Input
                        placeholder={t('search_placeholder')}
                        value={params.search}
                        onChange={(e) => setParams({ ...params, search: e.target.value, page: 0 })}
                    />
                </div>
                <div className="w-[200px]">
                    <Select
                        value={params.status}
                        onValueChange={(value) => setParams({ ...params, status: value, page: 0 })}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder={t('filter_status')} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('all')}</SelectItem>
                            <SelectItem value="pending">{t('status_pending')}</SelectItem>
                            <SelectItem value="done">{t('status_done')}</SelectItem>
                            <SelectItem value="refused">{t('status_refused')}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="rounded-md border p-0">
                {loading && !requests.length ? (
                    <div className="p-4">
                        <DataTableSkeleton columns={6} rows={5} />
                    </div>
                ) : error ? (
                    <div className="p-8 text-center text-destructive">
                        {t('error_fetching_requests')}
                    </div>
                ) : (
                    <DataTable
                        columns={columns}
                        data={requests}
                        pages={Math.ceil(count / params.page_size)}
                        params={params}
                        setParams={setParams}
                        showPagination={true}
                    />
                )}
            </div>

            <Dialog open={rejectDialog.open} onOpenChange={(open) => !open && setRejectDialog({ open: false, id: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('reject_request')}</DialogTitle>
                        <DialogDescription>
                            {t('reject_reason_desc')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder={t('reason_placeholder')}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRejectDialog({ open: false, id: null })}>
                            {t('cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleReject}
                            disabled={!rejectReason || !!actionLoading}
                        >
                            {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            {t('reject')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
